import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useAvailableDelegations } from '../../api/hooks/useAvailableDelegations';
import { createCompanySwitchSchema } from '../domain/schema';
import type { CompanySwitchForm } from '../domain/schema';
import { useDelegationsIds } from '../../hooks/useImpersonationScope';
import { useCompanySwitchScope } from './useCompanySwitchScope';
import { useCompanySwitchAccess } from './useCompanySwitchAccess';

type UseCompanySwitchModalParams = {
	isOpen: boolean;
	onClose: () => void;
};

export const useCompanySwitchModal = ({ isOpen, onClose }: UseCompanySwitchModalParams) => {
	const { t } = useTranslation();
	const delegationsIds = useDelegationsIds();

	const {
		userInfo,
		isSuperUser,
		hasDelegationAccessPermission,
		companies,
		isLoadingCompanies,
		capabilities,
	} = useCompanySwitchScope();

	const companyId = userInfo?.companyId ?? undefined;

	// Mirrors `isDelegationSelectionMandatory` below for the resolver to read at validation time —
	// see the note on `createCompanySwitchSchema`'s second parameter for why it can't be a value.
	const delegationsRequiredRef = useRef(false);

	const methods = useForm<CompanySwitchForm>({
		resolver: zodResolver(createCompanySwitchSchema(t, () => delegationsRequiredRef.current)),
		defaultValues: {
			company_id: companyId,
			delegations: delegationsIds ?? [],
		},
	});
	const { handleSubmit, reset, setValue, getValues, control } = methods;

	useLayoutEffect(() => {
		if (isOpen) {
			reset(
				{ company_id: companyId, delegations: delegationsIds ?? [] },
				{ keepDirty: false, keepTouched: false },
			);
		}
	}, [isOpen, companyId, delegationsIds, reset]);

	const watchedCompanyId = useWatch({ control, name: 'company_id' });

	// Deliberately NOT gated on `hasDelegationAccessPermission`, unlike the sidebar card's
	// `useDelegationOptions`: the permission is evaluated against the company the user is leaving,
	// while `mandatory` is a fact about the one they are entering. Gating it would hide the picker
	// from exactly the user this endpoint exists for.
	const { data: availableDelegations, isLoading: isLoadingDelegations } = useAvailableDelegations(
		watchedCompanyId,
		{ enabled: isOpen && watchedCompanyId !== undefined },
	);

	const delegationsOptions = availableDelegations?.options ?? [];
	const isDelegationSelectionMandatory = availableDelegations?.mandatory ?? false;

	useEffect(() => {
		delegationsRequiredRef.current = isDelegationSelectionMandatory;
	}, [isDelegationSelectionMandatory]);

	// A single available delegation is not a choice — preselect it so the user isn't asked to
	// confirm the only answer. Only when nothing is selected yet, so it never overwrites either the
	// session's current delegations or a pick the user just made.
	useEffect(() => {
		const options = availableDelegations?.options;
		if (options?.length !== 1) return;
		if (getValues('delegations').length > 0) return;

		const onlyValue = options[0]?.value;
		if (typeof onlyValue !== 'number') return;

		setValue('delegations', [onlyValue], { shouldDirty: true });
	}, [availableDelegations, getValues, setValue]);

	// `mandatory` outranks the permission: when the target company only exposes a subset of
	// delegations to this user, the field has to be reachable for the form to be satisfiable at
	// all. `capabilities.canSwitchDelegations` from the scope stays origin-company-bound, so it
	// can't answer this on its own.
	//
	// `isLoadingDelegations` keeps the field mounted between companies: the options are empty
	// while the new company's answer is in flight, and collapsing the field for that moment makes
	// it flicker out from under a user who is about to use it. The MultiSelect shows its own
	// loading state instead (`isLoadingOptions`).
	const canSwitchDelegations =
		isDelegationSelectionMandatory ||
		(hasDelegationAccessPermission && (isLoadingDelegations || delegationsOptions.length > 0));

	// Superuser-only id suffix carried over from the old fake-access modal — an admin debugging
	// aid, not shown to a regular multi-company user switching between their own companies.
	const companiesOptions = useMemo(
		() =>
			companies?.map((company) => ({
				value: company.value,
				label: isSuperUser ? `${company.label} - ${company.value}` : company.label,
			})) ?? [],
		[companies, isSuperUser],
	);

	// Empty already means "all delegations" (§ confirmed design), so switching company just
	// clears the selection rather than reconciling it against the new company's options.
	const onCompanySelect = useCallback(() => {
		setValue('delegations', []);
	}, [setValue]);

	const { switchAccess, isPending } = useCompanySwitchAccess({ onClose });

	const handleFormSubmit = (data: CompanySwitchForm) => {
		const companyName = companies?.find((c) => c.value === data.company_id)?.label ?? '';

		switchAccess({
			companyId: data.company_id,
			companyName,
			delegationsIds: data.delegations,
		});
	};

	return {
		methods,
		onSubmit: handleSubmit(handleFormSubmit),
		companiesOptions,
		isLoadingCompanies,
		delegationsOptions,
		isLoadingDelegations,
		// canSwitchCompany/canOpenSwitcher stay origin-scoped (correct — they gate the button
		// itself, decided before any company is picked); canSwitchDelegations is overridden with
		// the picked-company-aware value computed above.
		capabilities: { ...capabilities, canSwitchDelegations },
		isDelegationSelectionMandatory,
		onCompanySelect,
		userName: userInfo?.userName ?? '',
		// The confirm button stays in its loading state until `mandatory` is known: submitting
		// while the answer is in flight is precisely how an empty selection slips through.
		isPending: isPending || isLoadingDelegations,
	};
};
